import { Dispatch, SetStateAction, useRef, useState } from 'react'
import ReactCrop, { Crop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

interface CropImageProps {
  imageURL?: string
  aspect?: number
  setCropImageUrl: Dispatch<SetStateAction<string | null>>
  locked?: boolean
}

export default function CropImage({ imageURL, aspect, setCropImageUrl, locked }: CropImageProps) {
  const [crop, setCrop] = useState<Crop>()
  const imageRef = useRef<HTMLImageElement>(null)

  const getCroppedImg = async (image: HTMLImageElement, crop: Crop): Promise<{ fileBlob: Blob; fileUrl: string }> => {
    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    canvas.width = crop.width * scaleX
    canvas.height = crop.height * scaleY

    const ctx = canvas.getContext('2d')

    if (ctx) {
      ctx.imageSmoothingQuality = 'high'

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      )
    }

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const fileUrl = window.URL.createObjectURL(blob)
          resolve({ fileBlob: blob, fileUrl })
        }
      }, 'image/jpeg')
    })
  }

  const makeClientCrop = async (crop: Crop) => {
    if (imageRef && crop.width && crop.height) {
      const { fileUrl } = await getCroppedImg(imageRef.current as HTMLImageElement, crop)
      setCropImageUrl(fileUrl)
    }
  }

  const onImageLoaded = (image: HTMLImageElement | null) => {
    if (image) {
      const { width, height } = image
      const size = Math.min(width, height)
      const scaleX = (90 / width) * size
      const scaleY = (90 / height) * size

      setCrop({
        unit: '%',
        width: scaleX,
        height: scaleY,
        x: (100 - scaleX) / 2,
        y: (100 - scaleY) / 2,
      })
    }
  }

  return (
    <>
      {imageURL && (
        <ReactCrop crop={crop} onChange={(c) => setCrop(c)} aspect={aspect} onComplete={makeClientCrop} locked={locked}>
          <img src={imageURL} ref={imageRef} onLoad={() => onImageLoaded(imageRef.current)} alt="crop-tool" />
        </ReactCrop>
      )}
    </>
  )
}
